import { useLayoutEffect, useMemo, useRef } from "react";

import { type BodyPart } from "./BodyPart.model";
import {
  getPathLabel,
  isSelectableBodyPath,
  mountBodySvg,
  sanitizeBodySvg,
} from "./bodyPartSvg.utils";
import styles from "./BodyPartDiagram.module.scss";

interface Props {
  svgFile: string;
  selectedParts: BodyPart[];
  onPartsChange?: (parts: BodyPart[]) => void;
  type?: "front" | "back";
  preview?: boolean;
}

const BodyPartDiagram = ({
  svgFile,
  selectedParts,
  onPartsChange,
  type,
  preview,
}: Props) => {
  const diagramRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const onPartsChangeRef = useRef(onPartsChange);
  const selectedPartsRef = useRef(selectedParts);
  const defaultFillRef = useRef({ color: "#e2c39c", opacity: "1" });
  const svgMarkup = useMemo(() => sanitizeBodySvg(svgFile), [svgFile]);

  onPartsChangeRef.current = onPartsChange;
  selectedPartsRef.current = selectedParts;

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const svg = mountBodySvg(host, svgMarkup);
    const defaultPath = svg?.querySelector("path");
    if (defaultPath) {
      defaultFillRef.current = {
        color: defaultPath.style.fill || "#e2c39c",
        opacity: defaultPath.style.fillOpacity || "1",
      };
    }
  });

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host || preview) return;

    const handlePointerDown = (event: PointerEvent) => {
      const onChange = onPartsChangeRef.current;
      if (!onChange) return;

      const path = (event.target as Element | null)?.closest("path");
      if (!path || !host.contains(path) || !isSelectableBodyPath(path)) return;

      event.preventDefault();
      event.stopPropagation();

      const label = getPathLabel(path);
      if (!label) return;

      const diagram = diagramRef.current?.getBoundingClientRect();
      if (!diagram) return;

      const clickX = event.clientX - diagram.left;
      const clickY = event.clientY - diagram.top;
      const currentParts = selectedPartsRef.current;

      const filteredParts = currentParts.filter(item => {
        if (!item.position || item.type !== type) return true;
        const distance = Math.sqrt(
          Math.pow(item.position.absoluteX - clickX, 2) +
            Math.pow(item.position.absoluteY - clickY, 2),
        );
        return distance > 16;
      });

      if (filteredParts.length === currentParts.length) {
        onChange([
          ...filteredParts,
          {
            selectedPart: label,
            position: {
              absoluteX: clickX,
              absoluteY: clickY,
            },
            type: type ?? null,
          },
        ]);
      } else {
        onChange(filteredParts);
      }
    };

    host.addEventListener("pointerdown", handlePointerDown, true);
    return () => host.removeEventListener("pointerdown", handlePointerDown, true);
  }, [preview, svgMarkup, type]);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    mountBodySvg(host, svgMarkup);
    const filteredData = selectedPartsRef.current.filter(
      item => item.type === type,
    );
    const { color, opacity } = defaultFillRef.current;

    host.querySelectorAll("path").forEach(path => {
      if (!isSelectableBodyPath(path)) return;

      path.style.cursor = preview ? "" : "pointer";
      path.style.pointerEvents = preview ? "none" : "all";

      const label = getPathLabel(path);
      const isSelected = filteredData.some(item => item.selectedPart === label);
      if (isSelected) {
        path.style.fill = "#036ffc";
        path.style.fillOpacity = "0.4";
      } else {
        path.style.fill = color;
        path.style.fillOpacity = opacity;
      }
    });
  }, [preview, selectedParts, svgMarkup, type]);

  const markers = selectedParts.filter(item => item.type === type && item.position);

  return (
    <div ref={diagramRef} className={styles.diagram}>
      <div ref={hostRef} className={styles.host} />
      {markers.map((item, index) => (
        <div
          key={`${item.selectedPart}-${index}`}
          className={styles.marker}
          style={{
            left: item.position!.absoluteX - 8,
            top: item.position!.absoluteY - 8,
          }}
        />
      ))}
    </div>
  );
};

export default BodyPartDiagram;
